import React from 'react';
import { Sliders, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '../../../../components/ui/card';
import { getAllocationMethodBadgeClass } from '../../utils';
import type { AllocationRule } from '../../types';

interface RuleCardProps {
  rule: AllocationRule;
  selected?: boolean;
  onSelect?: () => void;
}

export default function RuleCard({ rule, selected, onSelect }: RuleCardProps) {
  const badgeStyle = getAllocationMethodBadgeClass(rule.type);

  return (
    <Card
      onClick={onSelect}
      className={`relative cursor-pointer transition-all duration-200 border-2 ${
        selected
          ? 'border-indigo-600 bg-indigo-50/20 dark:bg-indigo-950/20 shadow-md'
          : 'border-border hover:border-indigo-300 dark:hover:border-indigo-800'
      }`}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <Sliders className="w-4 h-4 text-indigo-600" />
            <h4 className="text-sm font-bold text-foreground">{rule.name}</h4>
          </div>
          {selected && <CheckCircle2 className="w-5 h-5 text-indigo-600" />}
        </div>

        <span
          className={`inline-block px-2.5 py-0.5 text-[11px] font-semibold rounded-full border ${badgeStyle}`}
        >
          {rule.type.toUpperCase().replace('_', ' ')}
        </span>

        <p className="text-xs text-muted-foreground leading-relaxed">
          {rule.description}
        </p>

        <div className="pt-2 border-t border-border/60 text-[11px] text-muted-foreground space-y-1">
          {rule.parameters.fixedLotValue !== undefined && (
            <div className="flex justify-between">
              <span>Fixed Lot:</span>
              <span className="font-semibold text-foreground">
                {rule.parameters.fixedLotValue} Lots
              </span>
            </div>
          )}
          {rule.parameters.multiplierFactor !== undefined && (
            <div className="flex justify-between">
              <span>Multiplier:</span>
              <span className="font-semibold text-foreground">
                {rule.parameters.multiplierFactor}x
              </span>
            </div>
          )}
          {rule.parameters.riskPercent !== undefined && (
            <div className="flex justify-between">
              <span>Risk Cap:</span>
              <span className="font-semibold text-foreground">
                {rule.parameters.riskPercent}% Equity
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span>Rounding Mode:</span>
            <span className="font-semibold text-foreground uppercase">
              {rule.parameters.roundLots || 'nearest'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
